import React, { useContext, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesDown, faMinus, faAnglesUp, faFire } from "@fortawesome/free-solid-svg-icons";
import { cn } from "../../lib/helpers";
import { SectionContext } from "./context";
import { Button } from "../button";

const PRIORITIES = [
  { value: "low", label: "Low", icon: faAnglesDown, color: "bg-green-100" },
  { value: "normal", label: "Normal", icon: false, color: "bg-yellow-300 xs:text-black" },
  { value: "high", label: "High", icon: faAnglesUp, color: "bg-orange-900" },
  { value: "urgent", label: "Urgent", icon: faFire, color: "bg-red" },
];

const STATUS = { IDLE: "idle", LOADING: "loading", SUCCESS: "success", ERROR: "error" };

export const ZendeskWebform = ({ section, prefill }) => {
  const sectionContext = useContext(SectionContext);
  const isDark = sectionContext.isDarkBg;

  const [fields, setFields] = useState({
    name: prefill?.name || "",
    email: prefill?.email || "",
    subject: prefill?.subject || "",
    message: prefill?.description || "",
    priority: "normal",
  });
  const [status, setStatus] = useState(STATUS.IDLE);
  const [errorMsg, setErrorMsg] = useState("");
  const [touched, setTouched] = useState({});

  // @todo useAccountCR() to get user email

  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const urlSubject = urlParams.get("subject");
      const urlDescription = urlParams.get("description");

      setFields((f) => ({
        ...f,
        ...(urlSubject && !prefill?.subject ? { subject: decodeURIComponent(urlSubject) } : {}),
        ...(urlDescription && !prefill?.description ? { message: decodeURIComponent(urlDescription) } : {}),
      }));
    }
  }, []);

  const handleChange = (e) =>
    setFields((f) => ({ ...f, [e.target.id]: e.target.value }));

  const handleBlur = (e) =>
    setTouched((t) => ({ ...t, [e.target.id]: true }));

  const validate = () => {
    if (!fields.name.trim()) return "Name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) return "A valid email is required.";
    if (!fields.subject.trim()) return "Subject is required.";
    if (!fields.message.trim()) return "Message is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, subject: true, message: true });
    const err = validate();
    if (err) { setErrorMsg(err); return; }

    setStatus(STATUS.LOADING);
    setErrorMsg("");

    try {
      const res = await fetch("/.netlify/functions/zen-desk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      setStatus(STATUS.SUCCESS);
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
      setStatus(STATUS.ERROR);
    }
  };

  const reset = () => {
    setFields({ name: "", email: "", subject: "", message: "", priority: "normal" });
    setStatus(STATUS.IDLE);
    setTouched({});
    setErrorMsg("");
  };

  const inputClass = (id) => cn(
    "w-full p-3 rounded-lg border focus:outline-none focus:ring-0 transition-colors",
    isDark ? "bg-dark-50 text-white placeholder-dark-30" : "bg-white text-dark-80 placeholder-dark-30",
    touched[id] && !fields[id]?.trim() ? "border-red" : "border-transparent"
  );

  if (status === STATUS.SUCCESS) {
    return (
      <div className="text-center py-12">
        <p className={cn("text-2xl font-semibold mb-2", isDark ? "text-white" : "text-dark-80")}>
          Message Sent
        </p>
        <p className={cn("mb-6", isDark ? "text-light-60" : "text-primary")}>
          We've received your request and will follow up at <strong>{fields.email}</strong>.
        </p>
        <button
          onClick={reset}
          className={cn("text-sm underline underline-offset-2", isDark ? "text-light-60 hover:text-white" : "text-primary hover:text-dark-80")}
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      {/* Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className={cn("font-medium mb-0.5", isDark ? "text-light-60" : "text-primary")} htmlFor="name">
            Full Name <span className="text-red">*</span>
          </label>
          <input
            className={inputClass("name")}
            id="name" type="text" placeholder="Your Name"
            value={fields.name} onChange={handleChange} onBlur={handleBlur}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={cn("font-medium mb-0.5", isDark ? "text-light-60" : "text-primary")} htmlFor="email">
            Email <span className="text-red">*</span>
          </label>
          <input
            className={inputClass("email")}
            id="email" type="email" placeholder="you@example.com"
            value={fields.email} onChange={handleChange} onBlur={handleBlur}
          />
        </div>
      </div>

      {/* Subject */}
      <div className="flex flex-col gap-1">
        <label className={cn("font-medium mb-0.5", isDark ? "text-light-60" : "text-primary")} htmlFor="subject">
          Subject <span className="text-red">*</span>
        </label>
        <input
          className={inputClass("subject")}
          id="subject" type="text" placeholder="How can we help?"
          value={fields.subject} onChange={handleChange} onBlur={handleBlur}
        />
      </div>

      {/* Priority */}
      <div className="flex flex-col gap-2">
        <span className={cn("font-medium mb-0.5", isDark ? "text-light-60" : "text-primary")}>
          Priority
        </span>
        <div className="flex gap-2">
          {PRIORITIES.map(({ value, label, icon, color }) => {
            const isSelected = fields.priority === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setFields((f) => ({ ...f, priority: value }))}
                className={cn(
                  "flex-1 flex justify-center items-center gap-2 py-2 px-3 rounded-lg border transition-colors font-medium text-white",
                  color,
                  isSelected ? "xs:bg-dark-50 xs:text-white" : null
                )}
              >
                <FontAwesomeIcon icon={icon} className="h-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message */}
      <div className="flex flex-col gap-1">
        <label className={cn("font-medium mb-0.5", isDark ? "text-light-60" : "text-primary")} htmlFor="message">
          Message <span className="text-red">*</span>
        </label>
        <textarea
          className={inputClass("message")}
          id="message" placeholder="Describe your issue or question in detail…"
          value={fields.message} onChange={handleChange} onBlur={handleBlur}
          rows={6}
        />
      </div>

      {/* Error banner */}
      {errorMsg && (
        <div className="flex items-start gap-2 bg-red bg-opacity-10 border-l-2 border-red rounded p-3 text-sm text-red" role="alert">
          <span>⚠</span><span>{errorMsg}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-center mt-4">
        <Button
          text={status === STATUS.LOADING ? "Sending…" : "Send Message"}
          deactivated={status === STATUS.LOADING}
          isBig={true}
          bgColor={isDark ? "bg-white" : "bg-black"}
          className={cn(
            "py-3 px-8 font-semibold rounded-lg transition-card duration-200 focus:outline-none focus:ring-0",
            status === STATUS.LOADING
              ? "bg-light-10 cursor-not-allowed"
              : false
          )}
        />
      </div>

      <p className="text-center mt-8 text-light-40 group">
        You can also email <a
          href="mailto:support@commonroom.io"
          className="group-hover:text-link transition-colors duration-300"
          target="_blank"
        >
          support@commonroom.io
        </a>
      </p>
    </form>
  );
};
